import moment from 'jalali-moment'
import React, { useState } from 'react'
import posthog from 'posthog-js'
import { useParams } from 'react-router-dom'
import { hasHashtag } from 'components/hashtagText/hashtags'
import { MoneyPoolTransactionTypes } from 'utilities/helper/moneyPools'
import deposit_icon from 'assets/images/rectangle/ic_deposit.svg'
import endurance_icon from 'assets/images/rectangle/ic_endurance.svg'
import west_vector_icon from 'assets/images/rectangle/west_vector.svg'
import CustomBottomSheet from '../../../../../components/bottomSheet'
import TransactionDetails from './transactionDetails'
import { ConvertJalaliDate } from '../../../../../utilities/helper/digitToMonth'
import {
    getTransactionStatusText,
    isCashin,
    TransactionStatusText,
} from '../../../../../utilities/helper/Funds'

interface IItemProps {
    item: IMoneyPoolTransactionResult
    isManager: boolean
    onHashtagClick?: (hashtag: string) => void
}

export default function Item({ item, isManager, onHashtagClick }: IItemProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const { id } = useParams()
    const statusText = TransactionStatusText[getTransactionStatusText(item)]
    const cashin = isCashin(item)

    const openDetails = () => {
        // The description is where hashtags live, so whether one is present is
        // the only way to tell a filing manager apart from someone checking an
        // amount. The text itself is deliberately not sent.
        posthog.capture('transaction_detail_opened', {
            moneypool_id: Number(id),
            is_cashin: cashin,
            has_description: !!item.tag,
            has_hashtag: hasHashtag(item.tag ?? ''),
            is_manager: isManager,
        })
        setIsOpen(true)
    }

    return (
        <>
            {/* Android item_moneypool_transaction.xml renders force-LTR:
                arrow icon + colored amount on the LEFT, name right-aligned
                on the RIGHT, status pill bottom-left / type bottom-right,
                detail link bottom-left / date|time bottom-right */}
            <div
                className="mx-4 my-1.5 rounded-[10px] border border-stroke bg-white p-3"
                dir="ltr"
            >
                {/* icon + amount (left) and destination name (right) */}
                <div className="flex flex-row items-baseline">
                    <img
                        className="h-4 w-4 self-center"
                        src={cashin ? deposit_icon : endurance_icon}
                        alt=""
                    />
                    <p
                        className={`ml-2 shrink-0 font-bold text-base ${
                            cashin ? 'text-success-1' : 'text-danger'
                        }`}
                    >
                        {item.amount && item.amount.toLocaleString()} تومان
                    </p>
                    <p className="ml-2 flex-1 text-right font-bold text-base text-[#565F6B]">
                        {item.poolship_name}
                    </p>
                </div>

                {/* status chip (left, mt-3) + type (right, mt-4) */}
                <div className="flex flex-row items-start justify-between">
                    {statusText ? (
                        <p className="mt-3 rounded-[10px] bg-blue-6 px-3 py-0.5 text-sm font-bold text-blue-5">
                            {statusText}
                        </p>
                    ) : (
                        <span className="mt-3" />
                    )}
                    <p className="mt-4 text-sm text-text-secondary">
                        {MoneyPoolTransactionTypes[item.type]}
                    </p>
                </div>

                {/* detail link (left) + date/time (right) */}
                <div className="mt-4 flex flex-row items-center justify-between">
                    <button
                        className="flex items-center gap-2"
                        onClick={openDetails}
                    >
                        <img
                            className="h-3.5 w-3.5"
                            src={west_vector_icon}
                            alt=""
                        />
                        <span className="text-sm font-bold text-blue-1">
                            جزئیات تراکنش
                        </span>
                    </button>
                    <p className="text-xs text-text-secondary" dir="rtl">
                        {(() => {
                            const date =
                                item?.jalali_time &&
                                ConvertJalaliDate(item.jalali_time)
                            const parsed = moment(
                                item.jalali_time,
                                'jYYYY-jMM-jDD HH:mm:ss.SSS'
                            )
                            const time = parsed.isValid()
                                ? parsed.format('HH:mm:ss')
                                : 'ساعت نامعتبر'
                            return time === '00:00:00'
                                ? date
                                : `${date} | ${time}`
                        })()}
                    </p>
                </div>
            </div>
            <CustomBottomSheet
                onDismiss={() => setIsOpen(false)}
                isOpen={isOpen}
            >
                <TransactionDetails
                    item={item}
                    isManager={isManager}
                    setIsOpen={() => setIsOpen(false)}
                    onHashtagClick={
                        onHashtagClick &&
                        ((hashtag) => {
                            // Close first: the filtered list renders behind
                            // this sheet, and leaving it open hides the result
                            // of the tap that caused it.
                            setIsOpen(false)
                            onHashtagClick(hashtag)
                        })
                    }
                />
            </CustomBottomSheet>
        </>
    )
}
